import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock as LockIcon, Mail as MailIcon, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { validateEmail } from '../lib/auth/validation';
import { getAuthErrorMessage } from '../lib/auth/errors';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const { resetPassword } = useAuth();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Réinitialiser le message d'erreur quand l'utilisateur tape
    if (message && !isLoading) {
      setMessage('');
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    // Validation à la perte de focus seulement si le champ a été touché
    if (email.trim() && emailTouched) {
      const validationError = validateEmail(email);
      if (validationError) {
        setMessage(validationError);
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation côté client avec la fonction centralisée
    const validationError = validateEmail(email);
    if (validationError) {
      setMessage(validationError);
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const { error } = await resetPassword(email.trim());

      if (error) {
        // Utiliser directement la fonction centralisée pour les erreurs serveur
        setMessage(getAuthErrorMessage(error));
        setIsSuccess(false);
      } else {
        setMessage('');
        setIsSuccess(true);
      }
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      setMessage("Une erreur inattendue s'est produite. Veuillez réessayer plus tard");
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Si l'envoi a réussi, afficher l'écran de confirmation
  if (isSuccess) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="flex flex-col gap-6 items-center justify-start max-w-[24rem] w-[24rem]">
          {/* Mail Icon */}
          <MailIcon className="size-8 text-zinc-600" />

          {/* Header */}
          <div className="flex flex-col gap-3 items-start justify-start text-center w-full">
            <div className="font-bold text-[1.875rem] text-zinc-950 w-full leading-[2.25rem]">
              Mot de passe oublié
            </div>
            <div className="font-normal text-[0.875rem] text-zinc-500 w-full leading-[1.25rem]">
              <p className="leading-[1.25rem] mb-0">
                Nous vous avons envoyé un lien sur votre email
              </p>
              <p className="leading-[1.25rem] mb-0">&nbsp;</p>
              <p className="font-medium leading-[1.5rem] text-[1rem] text-zinc-900">{email}</p>
            </div>
          </div>

          {/* Back to Login Link */}
          <div className="flex flex-col gap-6 items-center justify-start rounded-[0.5rem] w-full">
            <div className="flex flex-col gap-6 items-center justify-start w-full">
              <div className="flex flex-col gap-6 items-center justify-start w-full">
                <Link
                  to="/login"
                  className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors"
                >
                  Retourner sur l'écran de connexion
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Affichage du formulaire de saisie d'email
  return (
    <div className="bg-white min-h-screen flex items-center justify-center">
      <div className="flex flex-col gap-6 items-center justify-start max-w-[24rem] w-[24rem]">
        {/* Lock Icon */}
        <LockIcon className="size-8 text-zinc-600" />

        {/* Header */}
        <div className="flex flex-col gap-3 items-start justify-start text-center w-full">
          <div className="font-bold text-[1.875rem] text-zinc-950 w-full leading-[2.25rem]">
            Mot de passe oublié
          </div>
          <div className="font-normal text-[0.875rem] text-zinc-500 w-full leading-[1.25rem]">
            Veuillez entrer votre adresse e-mail et nous vous enverrons un lien pour réinitialiser
            votre mot de passe.
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-6 items-center justify-start rounded-[0.5rem] w-full">
          <form
            onSubmit={handleResetPassword}
            className="flex flex-col gap-2 items-start justify-start w-full"
          >
            {/* Email Input */}
            <div className="flex flex-col gap-2 items-start justify-start w-full">
              <div className="bg-white h-9 relative rounded-[0.375rem] w-full">
                <Input
                  type="email"
                  placeholder="E-mail"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  disabled={isLoading}
                  className={`h-9 px-3 py-1 border rounded-[0.375rem] shadow-[0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.05)] text-[0.875rem] placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                    message && !isSuccess
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-zinc-200 focus:ring-zinc-900'
                  }`}
                />
              </div>
            </div>
          </form>

          {/* Button and Back Link */}
          <div className="flex flex-col gap-6 items-center justify-start w-full">
            <Button
              type="submit"
              onClick={handleResetPassword}
              className="bg-zinc-900 hover:bg-zinc-800 text-neutral-50 h-9 px-4 py-2 rounded-[0.375rem] shadow-[0px_0.0625rem_0.1875rem_0px_rgba(0,0,0,0.1),0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.06)] w-full font-medium text-[0.875rem] leading-[1.25rem]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Envoi en cours...
                </>
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>

            {/* Back to Login Link */}
            <div className="flex flex-col gap-6 items-center justify-start w-full">
              <Link
                to="/login"
                className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors"
              >
                Retourner sur l'écran de connexion
              </Link>
            </div>
          </div>

          {/* Message d'erreur uniquement */}
          {message && !isSuccess && (
            <div className="text-sm p-3 rounded-md w-full bg-red-50 text-red-700 border border-red-200 fade-in">
              <div className="flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{message}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
